import type { SupplierDocumentCategory } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { extractErrorMessage } from '@/api/extract-error'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger
} from '@/components/ui/combobox'
import { FormSheet } from '@/components/ui/form-sheet'
import { Label } from '@/components/ui/label'
import { FileDropZone } from '@/domains/document'
import { captureException } from '@/domains/observability'

import { createSupplierDocument } from './api'

const DOC_MAX_BYTES = 200 * 1024 * 1024
const ACCEPT = 'application/pdf,image/png,image/jpeg,video/mp4'

/** 'logo' is set on the company form, not here, so it's left out of the picker. */
const CATEGORY_ORDER: ReadonlyArray<SupplierDocumentCategory> = [
  'facility',
  'equipment',
  'product',
  'management_team',
  'other'
]

const CATEGORY_LABELS: Record<SupplierDocumentCategory, string> = {
  facility: 'Facility',
  equipment: 'Equipment',
  product: 'Product',
  management_team: 'Management team',
  other: 'Other',
  logo: 'Logo'
}

export function AddDocumentSheet() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState<SupplierDocumentCategory>('facility')

  const createMutation = useMutation({
    mutationFn: createSupplierDocument,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['supplier-documents'] })
      toast.success('Document added')
      setCategory('facility')
      setOpen(false)
    },
    onError: error => {
      captureException(error)
      toast.error(extractErrorMessage(error))
    }
  })

  return (
    <FormSheet
      open={open}
      onOpenChange={next => {
        if (!createMutation.isPending) setOpen(next)
      }}
      trigger={
        <Button size='hero' className='w-full sm:w-auto'>
          <Plus className='size-4' />
          Add document
        </Button>
      }
      title='Add a document'
      description="Pick a category, then drop a PDF or image. It's added to your company documents as soon as it uploads."
    >
      <div className='flex flex-col gap-1.5'>
        <Label>Category</Label>
        <Combobox
          items={CATEGORY_ORDER as SupplierDocumentCategory[]}
          value={category}
          onValueChange={value => {
            if (value) setCategory(value as SupplierDocumentCategory)
          }}
          itemToStringLabel={(item: string) =>
            CATEGORY_LABELS[item as SupplierDocumentCategory]
          }
        >
          <ComboboxTrigger
            aria-label='Category'
            className='inline-flex h-9 w-full items-center justify-between rounded-md border border-border bg-page px-3 text-sm text-ink-soft transition-colors hover:bg-cream focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:outline-none'
          >
            {CATEGORY_LABELS[category]}
          </ComboboxTrigger>
          <ComboboxContent>
            <ComboboxList>
              {CATEGORY_ORDER.map(option => (
                <ComboboxItem key={option} value={option}>
                  {CATEGORY_LABELS[option]}
                </ComboboxItem>
              ))}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>

      <div className='flex flex-col gap-1.5'>
        <Label>File</Label>
        <FileDropZone
          accept={ACCEPT}
          maxBytes={DOC_MAX_BYTES}
          value={null}
          onUploaded={uploaded =>
            createMutation.mutate({
              documentId: uploaded.documentId,
              category
            })
          }
        />
        {createMutation.isPending && (
          <p className='text-[12px] text-ink-muted'>Saving…</p>
        )}
      </div>
    </FormSheet>
  )
}
